import { Fragment, ReactNode, useMemo, useState } from 'react';
import { FaFolder } from 'react-icons/fa6';
import { buildFolderTree, FolderNode, sortedChildren } from './folderTree.utils';
import {
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbSep,
  FolderCard,
  FolderCardCount,
  FolderCardName,
  FolderItemGrid,
} from './FolderTree.styles';

const resolveNode = <T,>(root: FolderNode<T>, path: string): FolderNode<T> | null => {
  if (!path) return root;
  let node: FolderNode<T> | undefined = root;
  for (const segment of path.split('/')) {
    node = node?.children.get(segment);
    if (!node) return null;
  }
  return node;
};

const collectItems = <T,>(node: FolderNode<T>): T[] => [...node.items, ...sortedChildren(node).flatMap(collectItems)];

type FolderTreeProps<T> = {
  items: T[];
  getKey: (item: T) => string;
  getFolderPath: (item: T) => string | null | undefined;
  renderItem: (item: T) => ReactNode;
  forceExpand?: boolean;
  minTile?: number;
  rootLabel?: string;
  itemName?: string;
  renderAdd?: (folderPath: string) => ReactNode;
};

const FolderTree = <T,>({
  items,
  getKey,
  getFolderPath,
  renderItem,
  forceExpand = false,
  minTile,
  rootLabel = 'All',
  itemName = 'item',
  renderAdd,
}: FolderTreeProps<T>) => {
  const root = useMemo(() => buildFolderTree(items, getFolderPath), [items, getFolderPath]);
  const [path, setPath] = useState('');

  const keyed = (item: T) => (
    <div key={getKey(item)} style={{ display: 'contents' }}>
      {renderItem(item)}
    </div>
  );

  if (forceExpand) {
    return (
      <FolderItemGrid minTile={minTile}>
        {renderAdd?.('')}
        {collectItems(root).map(keyed)}
      </FolderItemGrid>
    );
  }

  const current = resolveNode(root, path) ?? root;
  const atRoot = current === root;
  const segments = atRoot ? [] : current.path.split('/');
  const folders = sortedChildren(current);

  return (
    <div className="flex flex-col gap-3">
      {!atRoot && (
        <Breadcrumb aria-label="Folder breadcrumb">
          <BreadcrumbButton type="button" onClick={() => setPath('')}>
            {rootLabel}
          </BreadcrumbButton>
          {segments.map((segment, index) => {
            const target = segments.slice(0, index + 1).join('/');
            const islast = index === segments.length - 1;
            return (
              <Fragment key={target}>
                <BreadcrumbSep>/</BreadcrumbSep>
                <BreadcrumbButton type="button" disabled={islast} onClick={() => setPath(target)}>
                  {segment}
                </BreadcrumbButton>
              </Fragment>
            );
          })}
        </Breadcrumb>
      )}

      <FolderItemGrid minTile={minTile}>
        {renderAdd?.(current.path)}
        {folders.map((folder) => (
          <FolderCard key={folder.path} type="button" onClick={() => setPath(folder.path)}>
            <span className="folder-icon">
              <FaFolder size={38} />
            </span>
            <FolderCardName>{folder.name}</FolderCardName>
            <FolderCardCount>
              {folder.count} {itemName}
              {folder.count !== 1 ? 's' : ''}
            </FolderCardCount>
          </FolderCard>
        ))}
        {current.items.map(keyed)}
      </FolderItemGrid>
    </div>
  );
};

export default FolderTree;
